import PropTypes from 'prop-types';
import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import VideoGalleryItem from '@fec/frontend-aron/modules/media/VideoGalleryItem/VideoGalleryItem';
import VideoGalleryPlayer from '@fec/frontend-aron/modules/media/VideoGalleryPlayer/VideoGalleryPlayer.jsx';

import './VideoGallery.scss';

const VideoGallery = ({ assets, isWebview }) => {
  const { t } = useTranslation();
  const galleryListRef = useRef(null);
  const [isLeftVisible, setLeftVisible] = useState(false);
  const [isRightVisible, setRightVisible] = useState(false);
  const [activeVideoGalleryItem, setActiveVideoGalleryItem] = useState({
    urn: null,
    frontendId: null,
    title: null,
  });

  const scrollTo = (direction) => {
    if (direction === 'left') {
      galleryListRef.current.scrollLeft -= galleryListRef.current.offsetWidth;
    } else {
      galleryListRef.current.scrollLeft += galleryListRef.current.offsetWidth;
    }
  };

  useEffect(() => {
    const container = galleryListRef.current;
    const firstElement = container.firstChild;
    const lastElement = container.lastChild;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === firstElement) {
            setLeftVisible(!entry.isIntersecting);
          }
          if (entry.target === lastElement) {
            setRightVisible(!entry.isIntersecting);
          }
        });
      },
      {
        root: container,
        threshold: 1.0, // Fires when the entire element is visible
      },
    );

    if (firstElement) {
      observer.observe(firstElement);
    }
    if (lastElement) {
      observer.observe(lastElement);
    }

    return () => {
      if (firstElement) {
        observer.unobserve(firstElement);
      }
      if (lastElement) {
        observer.unobserve(lastElement);
      }
    };
  }, [galleryListRef.current]);

  return (
    <>
      {!isWebview && (
        <VideoGalleryPlayer
          assets={assets}
          activeVideoGalleryItem={activeVideoGalleryItem}
          setActiveVideoGalleryItem={setActiveVideoGalleryItem}
        />
      )}
      <div className="video-gallery">
        {isLeftVisible && (
          <button
            className="video-gallery__button video-gallery__button--left"
            onClick={() => scrollTo('left')}
            aria-label={t('videoGallery.previousVideo')}>
            <span className="video-gallery__button-icon"></span>
          </button>
        )}
        {/* Safari does not recognize ol/ul as lists if they have a list-style value of none (source: https://developer.mozilla.org/en-US/docs/Web/CSS/list-style#accessibility) */}
        <ul ref={galleryListRef} className="video-gallery__list" role="list">
          {assets.map((asset) => (
            <VideoGalleryItem
              key={asset.frontendId}
              asset={asset}
              frontendId={asset.frontendId}
              isWebview={isWebview}
              activeVideoGalleryItem={activeVideoGalleryItem}
              setActiveVideoGalleryItem={setActiveVideoGalleryItem}
            />
          ))}
        </ul>
        {isRightVisible && (
          <button
            className="video-gallery__button video-gallery__button--right"
            onClick={() => scrollTo('right')}
            aria-label={t('videoGallery.nextVideo')}>
            <span className="video-gallery__button-icon"></span>
          </button>
        )}
      </div>
    </>
  );
};

VideoGallery.propTypes = {
  assets: PropTypes.array,
  isWebview: PropTypes.bool,
};

export default VideoGallery;
